/**
 * ImportModal
 *
 * Modal for importing infrastructure data from CSV or JSON files.
 * Implements a two-phase workflow: validate first, then execute on confirmation.
 */

import { useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { useInfrastructureImport } from '../../hooks/use-infrastructure-import'
import type { DuplicateAction, ImportExecuteResult } from '../../types/infrastructure'
import { ErrorDisplay } from './import-modal/ErrorDisplay'
import { FileDropZone } from './import-modal/FileDropZone'
import { ImportSuccessDisplay } from './import-modal/ImportSuccessDisplay'
import { PhaseLoader } from './import-modal/PhaseLoader'
import { ValidationResultsDisplay } from './import-modal/ValidationResultsDisplay'
import { useImportFileHandlers } from './import-modal/use-import-file-handlers'

interface ImportModalProps {
  caseId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: (result: ImportExecuteResult) => void
}

interface ImportPhaseContentProps {
  phase: ReturnType<typeof useInfrastructureImport>['phase']
  loading: boolean
  error: string | null
  validationResult: ReturnType<typeof useInfrastructureImport>['validationResult']
  importResult: ReturnType<typeof useInfrastructureImport>['importResult']
  selectedFile: File | null
  dragActive: boolean
  fileInputRef: React.RefObject<HTMLInputElement>
  onDrag: (e: React.DragEvent) => void
  onDrop: (e: React.DragEvent) => void
  onBrowse: () => void
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onExecute: (action: DuplicateAction) => Promise<void>
  onClose: () => void
  onRetry: () => void
}

function ActivePhaseContent(props: ImportPhaseContentProps): React.ReactElement | null {
  const { t } = useTranslation('cases')
  const { phase } = props
  if (phase === 'idle') {
    return (
      <FileDropZone
        dragActive={props.dragActive}
        onDrag={props.onDrag}
        onDrop={props.onDrop}
        onBrowse={props.onBrowse}
        fileInputRef={props.fileInputRef}
        onInputChange={props.onInputChange}
      />
    )
  }
  if (phase === 'validating') {
    return (
      <PhaseLoader
        message={t('infrastructure.import.validating', { fileName: props.selectedFile?.name })}
      />
    )
  }
  if (phase === 'executing') {
    return <PhaseLoader message={t('infrastructure.import.importingRecords')} />
  }
  return null
}

function TerminalPhaseContent(props: ImportPhaseContentProps): React.ReactElement | null {
  const { phase } = props
  if (phase === 'validated' && props.validationResult) {
    return (
      <ValidationResultsDisplay
        result={props.validationResult}
        onExecute={props.onExecute}
        onCancel={props.onClose}
        loading={props.loading}
      />
    )
  }
  if (phase === 'complete' && props.importResult) {
    return <ImportSuccessDisplay result={props.importResult} onClose={props.onClose} />
  }
  if (phase === 'error' && props.error) {
    return <ErrorDisplay error={props.error} onRetry={props.onRetry} onClose={props.onClose} />
  }
  return null
}

function ImportPhaseContent(props: ImportPhaseContentProps): React.ReactElement {
  return (
    <>
      <ActivePhaseContent {...props} />
      <TerminalPhaseContent {...props} />
    </>
  )
}

export function ImportModal({
  caseId,
  isOpen,
  onClose,
  onSuccess,
}: ImportModalProps): React.ReactElement | null {
  const { t } = useTranslation('cases')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { phase, loading, error, validationResult, importResult, validate, execute, reset } =
    useInfrastructureImport(caseId)

  const { selectedFile, setSelectedFile, dragActive, handleDrag, handleDrop, handleInputChange } =
    useImportFileHandlers({ validate })

  const handleClose = useCallback((): void => {
    reset()
    setSelectedFile(null)
    onClose()
  }, [reset, setSelectedFile, onClose])

  const handleExecute = useCallback(
    async (duplicateAction: DuplicateAction): Promise<void> => {
      const result = await execute(duplicateAction)
      if (result) onSuccess?.(result)
    },
    [execute, onSuccess]
  )

  const handleBrowse = useCallback((): void => {
    fileInputRef.current?.click()
  }, [])

  const handleRetry = useCallback((): void => {
    reset()
    setSelectedFile(null)
  }, [reset, setSelectedFile])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-700">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
            {t('infrastructure.import.title')}
          </h2>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <ImportPhaseContent
            phase={phase}
            loading={loading}
            error={error}
            validationResult={validationResult}
            importResult={importResult}
            selectedFile={selectedFile}
            dragActive={dragActive}
            fileInputRef={fileInputRef}
            onDrag={handleDrag}
            onDrop={handleDrop}
            onBrowse={handleBrowse}
            onInputChange={handleInputChange}
            onExecute={handleExecute}
            onClose={handleClose}
            onRetry={handleRetry}
          />
        </div>
      </div>
    </div>
  )
}
